import type React from 'react'
import { useActiveObjectOptional } from '@/components/app/editor/active-object-context'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { ShapeData } from '@/lib/shapes/types/shape-data'

/**
 * The live canvas shape selection, for property panels that must reason about every selected
 * object rather than just one.
 *
 * The `activeObjects` context mirror is not trustworthy for this. It can go stale relative to the
 * real canvas selection, and `setActiveObject` replaces the whole selection with zero-or-one
 * object — so any caller that collapses the mirror leaves a panel believing a multi-selection is a
 * single object (or empty). The Edit-menu gating already avoids the mirror for exactly this reason
 * (WMPCM-6294) by reading the managers directly; controls that enable on "any eligible object is
 * selected" and then apply to every eligible object need the same guarantee, or a marquee /
 * shift-click multi-selection shows disabled controls or commits to only one shape.
 *
 * `activeObjects` is still consumed here — it is the subscription that re-renders the panel when
 * the selection moves — but the returned list is read from ShapeManager whenever one is wired.
 * Reading the ref during render matches the existing pattern for live selection reads in
 * `home.tsx` (`getSelectionSnapshotsForMenus`).
 */
export function useLiveSelectedShapes(
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
): ShapeData[] {
  const activeObjectCtx = useActiveObjectOptional()
  const activeObjects = activeObjectCtx?.activeObjects ?? []
  const manager = shapeManagerRef?.current

  if (
    typeof manager?.getSelectedShapeIds !== 'function' ||
    typeof manager?.getShapes !== 'function'
  ) {
    // No canvas wired (panel rendered standalone) — the context mirror is all there is.
    return activeObjects
  }

  const allShapes = manager.getShapes()

  return manager
    .getSelectedShapeIds()
    .map((id) => allShapes.find((shape) => shape.id === id))
    .filter((shape): shape is ShapeData => shape != null)
}
